import { z } from "zod";

import { NO_DISPLAY, YES_DISPLAY } from "@/intake-forms/constants/yes-no-display";

/** Matches the `answerOption.valueString` values on the statement-settings Questionnaire. */
const yesNoAnswer = z.enum([YES_DISPLAY, NO_DISPLAY]).nullable();

/**
 * The stored shape of the four answers.
 *
 * Nullable because that is how the answers rest: a practice that has never opened the form, and a
 * statement issued by one, both read as every answer unset, and the card renders those as the empty
 * marker. `emptyStatementSettings` builds exactly this shape.
 */
export const StatementSettingsFormSchema = z.object({
  providerAcceptAssignment: yesNoAnswer,
  directPaymentAuthorized: yesNoAnswer,
  providerSignatureOnFile: yesNoAnswer,
  patientSignatureOnFile: yesNoAnswer,
});

export type StatementSettingsFormType = z.infer<typeof StatementSettingsFormSchema>;

/** Shown under a field a biller left unanswered when they try to save. */
export const STATEMENT_SETTINGS_REQUIRED_MESSAGE = "Select Yes or No";

/** The same Yes/No answer, but one a save insists on. */
const answeredYesNo = z.enum([YES_DISPLAY, NO_DISPLAY], {
  required_error: STATEMENT_SETTINGS_REQUIRED_MESSAGE,
  invalid_type_error: STATEMENT_SETTINGS_REQUIRED_MESSAGE,
});

/**
 * What a save has to satisfy: every answer is Yes or No.
 *
 * Only Yes and No carry meaning on a billing document, so an answer is never written blank. The
 * drawers validate against this rather than the stored shape, which keeps unset a legal thing to
 * *read* — a practice that has answered nothing still loads and still renders as the empty marker —
 * while making it an illegal thing to *write*. Both drawers use it, so the practice's form and a
 * single statement's copy of the answers demand the same thing.
 */
export const StatementSettingsSaveSchema = z.object({
  providerAcceptAssignment: answeredYesNo,
  directPaymentAuthorized: answeredYesNo,
  providerSignatureOnFile: answeredYesNo,
  patientSignatureOnFile: answeredYesNo,
});
